"use client";

import { useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { SlidersHorizontalIcon } from "lucide-react";

import {
  Button,
  Sheet,
  SheetClose,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@tripcart/traveler-ui";

import type { SearchFilterBarDto } from "@tripcart/shared";

import { useTranslator } from "@/lib/translation-context";
import { parseState } from "./_filters-shared";
import { FilterPanel, type FilterOption } from "./_filter-panel.client";
import { scrollToResultsTopIfScrolledPast } from "./_scroll";
// Not `styles`: that name is the component's style-facet prop.
import css from "./_mobile-filters.client.module.css";

export function MobileFilters({
  destinations,
  styles,
  catalogMaxPrice,
  bar,
}: {
  destinations: FilterOption[];
  styles: FilterOption[];
  catalogMaxPrice?: number;
  /** The operator's arrangement, threaded straight through to `FilterPanel` —
   *  the drawer is the majority viewport, so an arrangement it does not receive
   *  is an arrangement most travelers never see. */
  bar?: SearchFilterBarDto;
}): React.ReactElement {
  const t = useTranslator();
  const router = useRouter();
  const sp = useSearchParams();
  const state = parseState((k) => sp.get(k) ?? undefined);
  const [, startTransition] = useTransition();
  const count =
    state.dest.length +
    state.style.length +
    state.months.length +
    (state.dur ? 1 : 0) +
    (sp.get("priceMin") || sp.get("priceMax") ? 1 : 0) +
    (state.q ? 1 : 0);

  function setStyle(slug: string | null): void {
    const p = new URLSearchParams(sp.toString());
    if (slug) p.set("style", slug);
    else p.delete("style");
    p.delete("page");
    startTransition(() => {
      router.replace(`?${p.toString()}`, { scroll: false });
    });
    scrollToResultsTopIfScrolledPast();
  }

  const activeStyle = state.style.length === 1 ? state.style[0] : null;
  const quick = styles.slice(0, 6);

  return (
    <div className={css.bar}>
      <Sheet>
        <SheetTrigger
          render={
            <Button variant="secondary" size="sm">
              <SlidersHorizontalIcon strokeWidth={2} aria-hidden />
              {t("Filters")}
              {count > 0 ? <span className={css.count}>{count}</span> : null}
            </Button>
          }
        />
        <SheetContent side="left" size="sm">
          <SheetHeader>
            <SheetTitle>{t("Filters")}</SheetTitle>
          </SheetHeader>
          <SheetBody className={css.sheetBody}>
            <FilterPanel
              destinations={destinations}
              styles={styles}
              idPrefix="m"
              catalogMaxPrice={catalogMaxPrice}
              bar={bar}
            />
          </SheetBody>
          <SheetFooter>
            <SheetClose
              render={
                <Button size="lg" className={css.showResults}>
                  {t("Show Results")}
                </Button>
              }
            />
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Button
        variant="secondary"
        size="sm"
        pressed={!activeStyle}
        onClick={() => setStyle(null)}
      >
        {t("All")}
      </Button>
      {quick.map((s) => (
        <Button
          key={s.slug}
          variant="secondary"
          size="sm"
          pressed={activeStyle === s.slug}
          onClick={() => setStyle(s.slug)}
        >
          {s.name}
        </Button>
      ))}
    </div>
  );
}
